import {
    AppComboBoxOnExecuteParameter,
    AppComboBoxItem,
    AppComboBoxOption,
    AppComboBoxPreviewOnExecuteParameter,
} from '@1js/acui-combobox';
import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computedFn } from 'mobx-utils/lib/computedFn';
import addFontMru from 'owa-editor-ribbon-secondary-ui/lib/actions/addFontMru';
import { getStore } from 'owa-editor-ribbon-secondary-ui/lib/store/store';
import { lazyUpdateSecondClassState, RibbonViewState } from 'owa-editor-ribbonplugin-store';
import type EditorViewState from 'owa-editor/lib/store/schema/EditorViewState';
import { callEditorApi } from 'owa-editor/lib/utils/callEditorApi';
import { callFormatApi } from 'owa-editor/lib/utils/callFormatApi';
import { getComposeRibbonId } from 'owa-editor/lib/utils/getComposeRibbonId';
import { FontPickerItem } from 'owa-format-options/lib/utils/FontPickerItem';
import loc from 'owa-localize';
import { FormatControlId } from 'owa-ribbon-ids';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import createWrapper from '../util/controlOnExecuteHandler';
import { startLivePreview, stopLivePreview } from '../util/livePreviewHandler';
import styles from './fontPicker.scss';
import { getKeytipString, constructKeytip } from 'owa-command-ribbon';
import { isFeatureEnabled } from 'owa-feature-flags';

let dividerKey = 0;
const MRU_PREFIX = 'mru_';
const DEFAULT_FONT_NAME = 'Calibri';

function getComboBoxOption(item: FontPickerItem, isMRUItem: boolean): AppComboBoxOption {
    const keyPrefix: string = isMRUItem ? MRU_PREFIX : '';
    return item.selectValue == '-'
        ? {
              type: 'AppComboBoxDivider',
              key: 'divider_' + (dividerKey++).toString(),
          }
        : {
              type: 'AppComboBoxItem',
              key: keyPrefix + item.selectValue,
              text: item.displayValue,
              ariaLabel: item.displayValue,
              styles: {
                  optionText: { fontFamily: item.submitValue },
                  rootHovered: styles.comboBoxOptionHovered,
              },
              commandValueId: item.submitValue,
          };
}

const getFontDropDownOptions = (): AppComboBoxOption[] => {
    const viewState = getStore().fontName;
    const mruItems = (viewState.mru || [])
        .map(family => viewState.items.filter(font => font.submitValue == family)[0])
        .filter(x => !!x);
    if (mruItems.length > 0) {
        mruItems.push({
            selectValue: '-',
            displayValue: '',
            submitValue: '',
        });
    }

    const appComboBoxOptions: AppComboBoxOption[] = [];
    mruItems.forEach(item => {
        appComboBoxOptions.push(getComboBoxOption(item, true));
    });

    viewState.items.forEach(item => {
        appComboBoxOptions.push(getComboBoxOption(item, false));
    });

    return appComboBoxOptions;
};

function getDefaultFontName() {
    const userOption = getUserConfiguration().UserOptions;
    return userOption?.ComposeFontName || DEFAULT_FONT_NAME;
}

function getSelectedKey(
    fontName: string | undefined,
    fontDropDownOptions: AppComboBoxOption[]
): string {
    let selectedKey: string = getDefaultFontName();
    for (let i = 0; i < fontDropDownOptions.length; i++) {
        const item = fontDropDownOptions[i] as AppComboBoxItem;
        // For regular fonts: fontName is passed as localized name, we compare it with item.text, which is localized name
        // For local fonts: fontName is passed as familyName/submitValue, we compare it with item.commandValueId, which is the familyName/submitValue
        if (fontName === item.text || fontName === item.commandValueId) {
            selectedKey = item.key;
            break;
        }
    }

    return selectedKey;
}

export const getFontNamePickerControl = computedFn(
    (
        editorViewState: EditorViewState | undefined,
        ribbonViewState: RibbonViewState | undefined,
        tabId: number | undefined
    ): RibbonControlProps => {
        const fontDropDownOptions = getFontDropDownOptions();
        const selectedKey = getSelectedKey(
            ribbonViewState?.formatViewState?.fontName,
            fontDropDownOptions
        );
        return {
            type: 'AppComboBoxProps',
            id: getComposeRibbonId(
                FormatControlId.FontNamePicker.toString(),
                editorViewState
            ).toString(),
            allowFreeForm: true,
            label: loc('Font'),
            width: 128,
            options: fontDropDownOptions,
            defaultSelectedKey: getDefaultFontName(),
            selectedKey,
            onExecute: (parameter: AppComboBoxOnExecuteParameter) => {
                if (!!editorViewState && !!ribbonViewState) {
                    stopLivePreview(editorViewState);
                    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                    // -> Error TS7053 (131,37): Element implicitly has an 'any' type because expression of type '"commandValueId"' can't be used to index type 'string | AppComboBoxPreviewNewValueParameter'.
                    // @ts-expect-error
                    const newFont = parameter.newValue['commandValueId'];
                    callFormatApi(editorViewState, 'setFontName', [newFont]);
                    addFontMru(getStore().fontName, newFont);

                    lazyUpdateSecondClassState.importAndExecute(
                        ribbonViewState.formatViewState,
                        callEditorApi(editorViewState, 'getStyleBasedFormatState')
                    );
                }
            },
            onPreviewExecute: (parameter: AppComboBoxPreviewOnExecuteParameter) => {
                startLivePreview(editorViewState, () => {
                    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                    // -> Error TS7053 (146,37): Element implicitly has an 'any' type because expression of type '"commandValueId"' can't be used to index type 'string | AppComboBoxPreviewNewValueParameter'.
                    // @ts-expect-error
                    const newFont = parameter.newValue['commandValueId'];
                    callFormatApi(editorViewState!, 'setFontName', [newFont]);
                });
            },
            onMenuOpen: createWrapper(editorViewState, ribbonViewState, undefined, false),
            styles: {
                optionsContainerWrapper: styles.fontPickerContainer,
            },
            onAfterMenuDismiss: () => {
                stopLivePreview(editorViewState);
            },
            keytipProps:
                tabId && isFeatureEnabled('mon-ribbon-keytips')
                    ? constructKeytip(
                          [getKeytipString(tabId)],
                          getKeytipString(FormatControlId.FontNamePicker)
                      )
                    : undefined,
        };
    }
);
